import { useTranslation } from "react-i18next"

import { useApolloClient } from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import {
  evictProjectPrice,
  evictRoomPrice
} from "shared/api/price/evictPriceCache"
import {
  invalidateProjectPriceCache,
  invalidateRoomPriceCache
} from "shared/api/price/modifyPriceCache"
import { DeleteResponseTypename } from "shared/api/types"
import { groupBy, intersection } from "shared/lib/arrayUtils"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { flattenTree } from "shared/lib/treeUtils"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import {
  ProductPositionTypename,
  ProjectChild,
  RoomTypename
} from "shared/types/Project"
import { DELETE_PROJECT_CHILDREN } from "shared/types/ProjectMutations"

import { evictProductPosition } from "entities/product-positions/lib/evictProductPosition"
import { findParentRooms } from "entities/project-children/lib/findProjectChildParent"
import {
  DeleteProjectChildrenMutation,
  DeleteProjectChildrenMutationVariables,
  useDeleteProjectChildrenMutation
} from "entities/projects/graphql/mutations.generated"
import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import {
  useProjectChildrenLazyQuery,
  useProjectsLazyQuery
} from "entities/projects/graphql/queries.generated"
import { evictRoom } from "entities/rooms/lib/evictRoom"

interface DeleteProjectChildrenArgs {
  projectID: string
  childrenToDelete: ProjectChild[]
}

export function useDeleteProjectChildren() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [deleteProjectChildren] = useDeleteProjectChildrenMutation()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const [fetchProjects] = useProjectsLazyQuery()
  const startProjectRequest = useProjectRequestMonitor()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Project.DELETE_PROJECT_CHILDREN.Start"),
      success: t("Request.Project.DELETE_PROJECT_CHILDREN.Success"),
      failure: t("Request.Project.DELETE_PROJECT_CHILDREN.Failure")
    }
  })

  return useEvent(
    ({ projectID, childrenToDelete }: DeleteProjectChildrenArgs) => {
      const flattenedChildren = flattenTree(childrenToDelete)
      const groupedByID = groupBy(flattenedChildren, c => c.id)

      const singularChildren = Object.entries(groupedByID).reduce(
        (acc: ProjectChild[], [, elements]) =>
          elements.length === 1 ? [...acc, elements[0]] : acc,
        []
      )

      const topLevelChildren = intersection(singularChildren, childrenToDelete)

      const groupedByType = groupBy(
        topLevelChildren,
        child => child?.__typename || "unknown"
      )

      const productPositions = groupedByType[ProductPositionTypename] || []
      const rooms = groupedByType[RoomTypename] || []

      const variables = {
        id: projectID,
        input: [
          ...rooms.map(room => ({ id: room.id, type: RoomTypename })),
          ...productPositions.map(productPosition => ({
            id: productPosition.id,
            type: ProductPositionTypename
          }))
        ]
      }

      variables.input.forEach(resource => {
        if (resource.type === ProductPositionTypename) {
          evictProductPosition(resource.id, apolloCache)
        }
        if (resource.type === RoomTypename) {
          evictRoom(resource.id, apolloCache)
        }
        const parentRooms = findParentRooms({
          projectID,
          childID: resource.id,
          cache: apolloCache
        })
        parentRooms?.forEach(room =>
          invalidateRoomPriceCache(room.id, apolloCache)
        )
        invalidateProjectPriceCache(projectID, apolloCache)
      })

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: DELETE_PROJECT_CHILDREN
      })

      return callMutation<
        DeleteProjectChildrenMutation,
        DeleteProjectChildrenMutationVariables,
        boolean
      >(deleteProjectChildren, {
        variables,
        transform: data => data.deleteProjectChildren.success,
        apolloOptions: {
          optimisticResponse: {
            __typename: "Mutation",
            deleteProjectChildren: {
              __typename: DeleteResponseTypename,
              success: true
            }
          },
          onCompleted: () => {
            variables.input.forEach(resource => {
              if (resource.type === ProductPositionTypename) {
                evictProductPosition(resource.id, apolloCache)
              }
              if (resource.type === RoomTypename) {
                evictRoom(resource.id, apolloCache)
              }
              const parentRooms = findParentRooms({
                projectID,
                childID: resource.id,
                cache: apolloCache
              })
              parentRooms?.forEach(room => evictRoomPrice(room.id, apolloCache))
            })
            completeProjectRequest()
            fetchProject({ variables: { id: projectID } })
            evictProjectHistory(projectID, apolloCache)
            raiseSuccessToast()
          },
          onError: () => {
            completeProjectRequest()
            fetchProject({ variables: { id: projectID } })
            evictProjectHistory(projectID, apolloCache)
            evictProjectPrice(projectID, apolloCache, true)
            fetchProjects()
            raiseErrorToast()
          }
        }
      })
    }
  )
}
